import React from 'react';
import { Edit, Trash2, Eye, User, Mail, Phone, BookOpen, IndianRupee, FileText, CreditCard } from 'lucide-react';
import { Teacher } from '../../types';
import { formatDate } from '../../utils/helpers';

interface TeacherListProps {
  teachers: Teacher[];
  loading?: boolean;
  onEdit: (teacher: Teacher) => void;
  onDelete: (teacher: Teacher) => void;
  onView: (teacher: Teacher) => void;
}

const TeacherList: React.FC<TeacherListProps> = ({
  teachers,
  loading = false,
  onEdit,
  onDelete,
  onView
}) => {
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Active':
        return 'bg-[#173322] text-[#8ff0b8]';
      case 'Inactive':
        return 'bg-[#b9ad9b] text-[#4a4235]';
      default:
        return 'bg-[#b9ad9b] text-[#4a4235]';
    }
  };

  if (loading) {
    return (
      <div className="bg-[#211c15] rounded-xl border border-[#3a3122]">
        <div className="flex items-center justify-center p-12">
          <div className="w-8 h-8 border-4 border-[#e2916d] border-t-transparent rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  if (teachers.length === 0) {
    return (
      <div className="bg-[#211c15] rounded-xl border border-[#3a3122]">
        <div className="text-center py-12">
          <User className="w-12 h-12 text-[#8a7f6e] mx-auto mb-4" />
          <p className="text-[#b9ad9b] text-lg">No teachers found</p>
          <p className="text-[#8a7f6e] text-sm">Add your first teacher to get started</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[#211c15] rounded-xl border border-[#3a3122]">
      <div className="p-6 border-b border-[#3a3122]">
        <h2 className="text-lg font-semibold text-[#f3ede2]">Teachers</h2>
        <p className="text-[#b9ad9b] text-sm mt-1">Manage school teaching staff</p>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-[#211c15]">
            <tr>
              <th className="text-left py-3 px-6 font-medium text-[#b9ad9b]">Teacher</th>
              <th className="text-left py-3 px-6 font-medium text-[#b9ad9b]">Contact</th>
              <th className="text-left py-3 px-6 font-medium text-[#b9ad9b]">Subject</th>
              <th className="text-left py-3 px-6 font-medium text-[#b9ad9b]">Department</th>
              <th className="text-left py-3 px-6 font-medium text-[#b9ad9b]">Qualification</th>
              <th className="text-left py-3 px-6 font-medium text-[#b9ad9b]">Salary</th>
              <th className="text-left py-3 px-6 font-medium text-[#b9ad9b]">Status</th>
              <th className="text-left py-3 px-6 font-medium text-[#b9ad9b]">Joined</th>
              <th className="text-left py-3 px-6 font-medium text-[#b9ad9b]">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#3a3122]">
            {teachers.map((teacher) => (
              <tr key={teacher.id} className="hover:bg-[#28221a]">
                <td className="py-4 px-6">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-[#332217] rounded-full flex items-center justify-center">
                      {teacher.photoURL ? (
                        <img
                          src={teacher.photoURL}
                          alt={teacher.name}
                          className="w-10 h-10 rounded-full object-cover"
                        />
                      ) : (
                        <User className="w-5 h-5 text-[#f0a67d]" />
                      )}
                    </div>
                    <div>
                      <div className="font-medium text-[#f3ede2]">{teacher.name}</div>
                      <div className="text-sm text-[#b9ad9b]">ID: {teacher.teacherId}</div>
                    </div>
                  </div>
                </td>
                <td className="py-4 px-6">
                  <div className="space-y-1">
                    <div className="flex items-center space-x-2 text-sm">
                      <Mail className="w-4 h-4 text-[#8a7f6e]" />
                      <span className="text-[#b9ad9b]">{teacher.email}</span>
                    </div>
                    <div className="flex items-center space-x-2 text-sm">
                      <Phone className="w-4 h-4 text-[#8a7f6e]" />
                      <span className="text-[#b9ad9b]">{teacher.phone}</span>
                    </div>
                  </div>
                </td>
                <td className="py-4 px-6">
                  <div className="flex items-center space-x-2">
                    <BookOpen className="w-4 h-4 text-[#8a7f6e]" />
                    <span className="font-medium text-[#f3ede2]">{teacher.subject}</span>
                  </div>
                </td>
                <td className="py-4 px-6">
                  <span className="text-[#b9ad9b]">{teacher.department || 'Not assigned'}</span>
                </td>
                <td className="py-4 px-6">
                  <div>
                    <span className="text-[#b9ad9b]">{teacher.qualification}</span>
                    {teacher.documents && teacher.documents.length > 0 && (
                      <div className="flex items-center space-x-1 mt-1">
                        <FileText className="w-3 h-3 text-[#4ade80]" />
                        <span className="text-xs text-[#4ade80]">{teacher.documents.length} docs</span>
                      </div>
                    )}
                  </div>
                </td>
                <td className="py-4 px-6">
                  <div>
                    <div className="flex items-center space-x-2">
                      <IndianRupee className="w-4 h-4 text-[#8a7f6e]" />
                      <span className="font-medium text-[#f3ede2]">₹{teacher.salary.toLocaleString()}</span>
                    </div>
                    {teacher.bankDetails && (
                      <div className="flex items-center space-x-1 mt-1">
                        <CreditCard className="w-3 h-3 text-[#4ade80]" />
                        <span className="text-xs text-[#4ade80]">Bank linked</span>
                      </div>
                    )}
                  </div>
                </td>
                <td className="py-4 px-6">
                  <span className={`px-2 py-1 rounded-full text-sm ${getStatusColor(teacher.status)}`}>
                    {teacher.status}
                  </span>
                </td>
                <td className="py-4 px-6 text-[#b9ad9b] text-sm">
                  {formatDate(teacher.dateOfJoining)}
                </td>
                <td className="py-4 px-6">
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => onView(teacher)}
                      className="p-2 text-[#8a7f6e] hover:text-[#60a5fa] hover:bg-[#16212b] rounded-lg transition-colors"
                      title="View Details"
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => onEdit(teacher)}
                      className="p-2 text-[#8a7f6e] hover:text-[#f0b64c] hover:bg-[#332711] rounded-lg transition-colors"
                      title="Edit Teacher"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => onDelete(teacher)}
                      className="p-2 text-[#8a7f6e] hover:text-[#f38b83] hover:bg-[#341a17] rounded-lg transition-colors"
                      title="Delete Teacher"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default TeacherList;